import { useRef, useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { motion } from "framer-motion";
import {
  ArrowLeft, Satellite, Radio, Building2, Activity, Layers, Settings, Crosshair,
} from "lucide-react";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// Turns the dark base style into the cyberpunk / neon look from the reference.
function applyCyberpunk(map) {
  const layers = map.getStyle().layers || [];
  let labelLayerId;
  for (const l of layers) {
    if (l.type === "symbol" && l.layout && l.layout["text-field"]) { labelLayerId = l.id; break; }
  }
  for (const l of layers) {
    try {
      if (l.type === "background") map.setPaintProperty(l.id, "background-color", "#02050f");
      if (l.id === "land" && l.type === "fill") map.setPaintProperty(l.id, "fill-color", "#03070f");
      if (l.id === "water") map.setPaintProperty("water", "fill-color", "#020b14");
      if (l.type === "line" && /road|street|bridge|tunnel|motorway|trunk|primary|secondary|tertiary/i.test(l.id)) {
        map.setPaintProperty(l.id, "line-color", "#00f0ff");
        map.setPaintProperty(l.id, "line-blur", 1.1);
        map.setPaintProperty(l.id, "line-opacity", 0.95);
        map.setPaintProperty(l.id, "line-width", [
          "interpolate", ["linear"], ["zoom"], 10, 0.4, 15, 1.8, 18, 4,
        ]);
      }
    } catch (_) { /* layer not paintable */ }
  }
  if (!map.getLayer("3d-buildings")) {
    map.addLayer(
      {
        id: "3d-buildings",
        source: "composite",
        "source-layer": "building",
        filter: ["==", "extrude", "true"],
        type: "fill-extrusion",
        minzoom: 14,
        paint: {
          "fill-extrusion-color": [
            "interpolate", ["linear"], ["get", "height"],
            0, "#ff2d95", 30, "#b026ff", 90, "#5b7cff", 200, "#22d3ee",
          ],
          "fill-extrusion-height": ["interpolate", ["linear"], ["zoom"], 14, 0, 15.5, ["get", "height"]],
          "fill-extrusion-base": ["get", "min_height"],
          "fill-extrusion-opacity": 0.92,
        },
      },
      labelLayerId
    );
  }
}

const HUD_TOOLS = [
  { id: "satelite", label: "SATÉLITE", Icon: Satellite },
  { id: "trafico", label: "TRÁFICO", Icon: Radio },
  { id: "edificios", label: "EDIFICIOS", Icon: Building2 },
  { id: "analisis", label: "ANÁLISIS", Icon: Activity },
  { id: "leyenda", label: "LEYENDA", Icon: Layers },
  { id: "sistema", label: "SISTEMA", Icon: Settings },
];

export const MapboxView = ({ place, onBack }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [buildings, setBuildings] = useState(true);
  const [satellite, setSatellite] = useState(false);
  const [tool, setTool] = useState("edificios");

  useEffect(() => {
    if (!containerRef.current || !place || !mapboxgl.accessToken) return undefined;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [place.lng, place.lat],
      zoom: 14,
      pitch: 58,
      bearing: -18,
      antialias: true,
      attributionControl: false,
    });
    mapRef.current = map;
    map.on("style.load", () => {
      applyCyberpunk(map);
      new mapboxgl.Marker({ color: "#00f0ff" }).setLngLat([place.lng, place.lat]).addTo(map);
      setTimeout(() => map.resize(), 300);
      map.flyTo({ center: [place.lng, place.lat], zoom: 16.5, pitch: 66, bearing: 24, duration: 4500, essential: true });
    });
    return () => map.remove();
  }, [place]);

  const recenter = () => {
    const map = mapRef.current;
    if (map && place) map.flyTo({ center: [place.lng, place.lat], zoom: 16.5, pitch: 66, bearing: 24, duration: 2500 });
  };

  const handleTool = (id) => {
    setTool(id);
    const map = mapRef.current;
    if (!map) return;
    if (id === "edificios" && map.getLayer("3d-buildings")) {
      const next = !buildings;
      setBuildings(next);
      map.setLayoutProperty("3d-buildings", "visibility", next ? "visible" : "none");
    }
    if (id === "satelite") {
      const next = !satellite;
      setSatellite(next);
      map.setStyle(next ? "mapbox://styles/mapbox/satellite-streets-v12" : "mapbox://styles/mapbox/dark-v11");
      map.once("style.load", () => { if (!next) applyCyberpunk(map); });
    }
  };

  return (
    <div className="absolute inset-0" data-testid="mapbox-view">
      <div ref={containerRef} className="absolute inset-0" />

      {/* scanline / vignette overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 220px rgba(0,0,0,0.85)" }} />

      {/* top-left: back + location breadcrumb */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
        <button
          data-testid="map-back-btn"
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/60 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 backdrop-blur-md transition-all text-sm font-medium w-max"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al globo
        </button>
        <div className="px-3 py-2 rounded-lg bg-black/55 border border-cyan-500/15 backdrop-blur-md font-mono text-[11px] tracking-[0.15em] text-cyan-200/90 uppercase max-w-[70vw] truncate">
          <span className="text-cyan-500/60">VISTA ·&nbsp;</span>{place?.name}
        </div>
      </div>

      {/* top-right: system status */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2 px-3 py-2 rounded-full bg-black/55 border border-cyan-500/20 backdrop-blur-md">
        <span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee] animate-pulse" />
        <span className="font-mono text-[10px] tracking-[0.25em] text-cyan-300 uppercase">System Online</span>
      </div>

      {/* left status rail */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col gap-3 font-mono text-[9px] tracking-[0.3em] text-cyan-400/70 uppercase">
        <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />Live</span>
        <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400" />Active</span>
        <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />Online</span>
      </div>

      {/* recenter */}
      <button
        data-testid="map-recenter-btn"
        onClick={recenter}
        className="absolute right-4 bottom-28 z-10 w-11 h-11 rounded-full bg-black/60 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 backdrop-blur-md flex items-center justify-center transition-all"
      >
        <Crosshair className="w-5 h-5" />
      </button>

      {/* bottom toolbar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 sm:gap-2 px-2 py-2 rounded-2xl bg-black/65 border border-cyan-500/20 backdrop-blur-xl"
        data-testid="map-toolbar"
      >
        {HUD_TOOLS.map(({ id, label, Icon }) => {
          const isActive = tool === id || (id === "edificios" && buildings) || (id === "satelite" && satellite);
          return (
            <button
              key={id}
              data-testid={`tool-${id}`}
              onClick={() => handleTool(id)}
              className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-all ${
                isActive ? "bg-cyan-500/20 text-cyan-200 shadow-[0_0_15px_rgba(34,211,238,0.3)]" : "text-slate-400 hover:text-cyan-200 hover:bg-white/5"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="font-mono text-[8px] tracking-[0.15em]">{label}</span>
            </button>
          );
        })}
      </motion.div>
    </div>
  );
};
